import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAthlete } from '@/hooks/useAthlete'
import { useAthleteProgram } from '@/hooks/usePrograms'
import { useAthleteReport } from '@/hooks/useAthleteReport'
import { useTestingResults } from '@/hooks/useTestingResults'
import { TESTING_METRICS } from '@/lib/testingMetrics'
import {
  bestPerSession, rowAsymmetry, changeFromBaseline, coefficientOfVariation, readChange,
} from '@/lib/testingAnalytics'
import { Button } from '@/components/ui/Button'
import { Logo } from '@/components/Logo'
import { cn } from '@/lib/cn'

const round = (v: number, dp = 1) => Math.round(v * 10 ** dp) / 10 ** dp
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString() : '—')

/**
 * Parent-facing progress report. Deliberately leads with what the athlete has
 * DONE (sessions, lifts) before test scores, so the report still says something
 * meaningful when VALD numbers are flat or the athlete is between tests.
 * Print-friendly: "Print / Save as PDF" produces a clean one-pager.
 */
export default function AthleteReport() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const athleteQ = useAthlete(id, true)
  const programQ = useAthleteProgram(id, true)
  const reportQ = useAthleteReport(id)
  const testsQ = useTestingResults(id)

  const [note, setNote] = useState('')
  const [noteSaved, setNoteSaved] = useState(false)
  useEffect(() => {
    supabase.from('athletes').select('report_note').eq('id', id).maybeSingle()
      .then(({ data }) => setNote(data?.report_note ?? ''))
  }, [id])
  const saveNote = async () => {
    const { error } = await supabase.from('athletes').update({ report_note: note }).eq('id', id)
    if (error) return console.error('Could not save note:', error.message)
    setNoteSaved(true)
    setTimeout(() => setNoteSaved(false), 1500)
  }

  const r = reportQ.data
  const sessions = bestPerSession(testsQ.data ?? [])

  // Testing highlights: latest vs first recorded, biggest improvement first.
  const testHighlights = TESTING_METRICS.flatMap((m) => {
    const rows = sessions.filter((s) => s.test_id === m.id)
    if (rows.length < 2) return []
    const latest = rows[0]
    const baseline = rows[rows.length - 1]
    return m.fields.flatMap((f) => {
      const isAsym = f.key === 'asymmetry'
      const now = isAsym ? rowAsymmetry(latest) : latest.values?.[f.key] ?? null
      const first = isAsym ? rowAsymmetry(baseline) : baseline.values?.[f.key] ?? null
      if (now == null || first == null) return []
      const pct = changeFromBaseline(isAsym ? Math.abs(now) : now, isAsym ? Math.abs(first) : first)
      if (pct == null) return []
      // For asymmetry a fall is the good direction.
      const better = isAsym ? pct < 0 : pct > 0
      // The athlete's own variation on this metric decides whether a move is real.
      const cv = coefficientOfVariation(
        rows.map((s2) => (isAsym ? rowAsymmetry(s2) : s2.values?.[f.key] ?? null))
          .map((v) => (v != null && isAsym ? Math.abs(v) : v))
      )
      const reading = readChange(pct, cv, isAsym)
      return [{
        label: `${m.label} — ${f.label}`,
        now: `${round(isAsym ? Math.abs(now) : now, 2)}${f.unit ?? m.unit}`,
        first: `${round(isAsym ? Math.abs(first) : first, 2)}${f.unit ?? m.unit}`,
        pct, better, since: baseline.date, reading,
      }]
    })
  }).sort((a, b) => Number(b.better) - Number(a.better) || Math.abs(b.pct) - Math.abs(a.pct))

  const wins = testHighlights.filter((h) => h.reading.verdict === 'improved').slice(0, 3)
  // Show real declines too, with context — hiding them would be its own dishonesty.
  const dips = testHighlights.filter((h) => h.reading.verdict === 'declined').slice(0, 2)
  const topLifts = (r?.lifts ?? []).slice(0, 6)

  if (athleteQ.isLoading || reportQ.isLoading) {
    return <p className="p-10 text-center text-muted">Building report…</p>
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-5 py-6 print:max-w-none print:px-0">
      {/* Screen-only controls */}
      <div className="mb-6 flex items-center gap-3 print:hidden">
        <button onClick={() => navigate(`/athlete/${id}`)} className="text-gold hover:-translate-x-0.5" aria-label="Back">‹</button>
        <h1 className="flex-1 font-display text-xl tracking-wide">Progress Report</h1>
        <Button size="sm" onClick={() => window.print()}>Print / Save as PDF</Button>
      </div>

      {/* Report header */}
      <header className="mb-6 border-b-2 border-gold/50 pb-4 print:mb-3 print:pb-2">
        <Logo className="mb-3 h-8 print:mb-1.5 print:h-6" />
        <h2 className="font-display text-3xl tracking-wide">{athleteQ.data?.name ?? 'Athlete'}</h2>
        {programQ.data?.currentWeek && (
          <p className="text-sm text-muted">Week {programQ.data.currentWeek} of training</p>
        )}
        <p className="mt-1 text-xs text-subtle">Report generated {new Date().toLocaleDateString()}</p>
      </header>

      {/* Headline effort numbers — always something to show */}
      <section className="mb-7 grid grid-cols-2 gap-3 sm:grid-cols-4 print:mb-4 print:grid-cols-4 print:gap-2">
        <Tile label="Sessions trained" value={String(r?.sessionsTrained ?? 0)} />
        <Tile label="Exercises trained" value={String(r?.exercisesLogged ?? 0)} />
        <Tile label="Sets recorded" value={String(r?.setsLogged ?? 0)} />
        <Tile label="New personal bests" value={String(r?.improved.length ?? 0)} accent />
      </section>

      {/* Strength — the part that stays positive even when testing is flat */}
      <section className="mb-7 print:mb-4">
        <h3 className="mb-2 border-b border-border pb-1 font-display text-xl tracking-wide text-gold print:mb-1 print:text-lg">
          Strength in the gym
        </h3>
        {topLifts.length === 0 ? (
          <p className="text-sm text-muted">No weights logged yet.</p>
        ) : (
          <>
            {r && r.improved.length > 0 && (
              <p className="mb-3 rounded-btn border border-success/35 bg-success/10 px-3.5 py-2 text-sm text-success print:mb-2 print:py-1.5 print:text-[12px]">
                🏆 {r.improved.length} personal best{r.improved.length === 1 ? '' : 's'} since{' '}
                {fmtDate(r.firstLog)} — biggest gain{' '}
                {round(Math.max(...r.improved.map((l) => l.gain ?? 0)))}kg on{' '}
                {r.improved.slice().sort((a, b) => (b.gain ?? 0) - (a.gain ?? 0))[0].name}.
              </p>
            )}
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="font-condensed text-[10px] uppercase tracking-[0.15em] text-muted">
                  <th className="border-b border-border px-2 py-1.5 text-left">Exercise</th>
                  <th className="border-b border-border px-2 py-1.5 text-right">Best lift</th>
                  <th className="border-b border-border px-2 py-1.5 text-right">Improvement</th>
                </tr>
              </thead>
              <tbody>
                {topLifts.map((l) => (
                  <tr key={l.exerciseId} className="border-b border-border/50">
                    <td className="px-2 py-1.5 print:py-1">{l.name}</td>
                    <td className="px-2 py-1.5 text-right font-display text-base tracking-wide print:py-1">{round(l.best)}kg</td>
                    <td className={cn('px-2 py-1.5 text-right text-xs print:py-1', (l.gain ?? 0) > 0 ? 'text-success' : 'text-subtle')}>
                      {l.gain == null ? 'first block' : l.gain > 0 ? `▲ ${round(l.gain)}kg` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </section>

      {/* Testing */}
      <section className="mb-7 print:mb-4">
        <h3 className="mb-2 border-b border-border pb-1 font-display text-xl tracking-wide text-gold print:mb-1 print:text-lg">
          Physical testing
        </h3>
        {sessions.length === 0 ? (
          <p className="text-sm text-muted">No testing results recorded yet.</p>
        ) : wins.length === 0 ? (
          <p className="text-sm text-muted">
            Testing is recorded but hasn’t moved much this block — the gym numbers above are the
            better measure of progress right now.
          </p>
        ) : (
          <div className="space-y-2">
            {wins.map((h) => (
              <div key={h.label} className="flex flex-wrap items-baseline gap-x-3 rounded-btn bg-surface-2 px-3.5 py-2 print:py-1.5">
                <span className="flex-1 text-sm">{h.label}</span>
                <span className="font-display text-lg tracking-wide">{h.now}</span>
                <span className="text-xs text-success">
                  ▲ {round(Math.abs(h.pct))}% since {fmtDate(h.since)}
                </span>
                <span className="w-full text-[11px] text-subtle">was {h.first}</span>
              </div>
            ))}
          </div>
        )}
        {dips.length > 0 && (
          <div className="mt-3 space-y-2">
            {dips.map((h) => (
              <div key={h.label} className="rounded-btn border border-border bg-surface-2 px-3.5 py-2.5 print:py-1.5">
                <div className="flex flex-wrap items-baseline gap-x-3">
                  <span className="flex-1 text-sm">{h.label}</span>
                  <span className="font-display text-lg tracking-wide">{h.now}</span>
                  <span className="text-xs text-muted">was {h.first}</span>
                </div>
                {/* Generic wording only when the coach hasn't explained it themselves. */}
                {!note.trim() && (
                  <p className="mt-1 text-[12px] leading-relaxed text-subtle">{h.reading.note}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Coach's own explanation — the app describes what changed, the coach
          explains why. Editable on screen, printed as part of the report. */}
      {note.trim() && (
        <section className="mb-7 print:mb-4">
          <h3 className="mb-2 border-b border-border pb-1 font-display text-xl tracking-wide text-gold print:mb-1 print:text-lg">
            From the coach
          </h3>
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-text">{note}</p>
        </section>
      )}

      <div className="mb-7 print:hidden">
        <label className="mb-1.5 block font-condensed text-[11px] uppercase tracking-[0.2em] text-muted">
          Coach note for this report
        </label>
        <p className="mb-1.5 text-[11px] text-subtle">
          Replaces the app's generic wording under any decrease — your explanation, not a guess.
        </p>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. This block focused on speed and power, so maximal strength work was deliberately reduced — we'd expect the mid-thigh pull to hold rather than climb."
          className="h-24 w-full resize-none rounded-input border border-border-strong bg-white/[.03] p-3 text-sm text-text outline-none placeholder:text-subtle focus:border-gold"
        />
        <div className="mt-2 flex items-center gap-3">
          <Button size="sm" variant="secondary" onClick={saveNote}>Save note</Button>
          {noteSaved && <span className="text-xs text-success">Saved.</span>}
          <span className="text-xs text-subtle">Appears in the printed report.</span>
        </div>
      </div>

      <p className="border-t border-border pt-3 text-[11px] leading-snug text-subtle print:pt-2 print:text-[9px]">
        Best lift figures come from weights recorded in training sessions. Testing figures are
        measured on VALD force plates. Test scores naturally move up and down between sessions
        depending on what the training block is developing and how rested an athlete is on the
        day — a single result is less meaningful than the direction over time. Questions? Speak
        to your coach.
      </p>
    </div>
  )
}

function Tile({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className={cn('rounded-card border px-3 py-2.5 print:py-1.5', accent ? 'border-gold/40 bg-gold/[.07]' : 'border-border bg-surface-2')}>
      <p className="font-condensed text-[10px] uppercase tracking-[0.15em] text-muted">{label}</p>
      <p className={cn('font-display text-2xl tracking-wide print:text-xl', accent && 'text-gold')}>{value}</p>
    </div>
  )
}
